import { useCallback, useMemo, useState } from 'react'
import { ArrowUp, LibraryBig, Paperclip } from 'lucide-react'
import { toast } from 'sonner'
import type { Classroom, ClassroomMaterial } from '../api/backend'
import { chatInputActionsRef } from './ChatInput'
import type { ChatAttachment } from '../state/types'
import { buildStorageFileUrl, generateId } from '../state/types'
import { useAutoResizeTextarea } from '../hooks/useAutoResizeTextarea'
import { ATTACHMENT_ACCEPT, useAttachmentUpload } from '../hooks/useAttachmentUpload'
import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@neo-agents/ui'
import { AttachmentList } from './AttachmentList'
import { ClassMaterialsDropdown } from './ClassMaterialsDropdown'
import { getClassroomPalette } from '../lib/classroom-theme'
import { useAtomValue } from 'jotai'
import { isDarkAtom } from '../state/store'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { FilePreview, isPreviewableFile } from './FilePreview'
import { ImagePreview } from './ImagePreview'

interface ClassHomeProps {
  classroom: Classroom
  onStarterPromptClick?: (text: string) => void
}

export function ClassHome({ classroom, onStarterPromptClick }: ClassHomeProps) {
  const isDark = useAtomValue(isDarkAtom)
  const { memberRole } = useWorkspace()
  const isEnded = classroom.status === 'ENDED'
  const isReadOnlyAdmin = memberRole === 'ADMIN'
  const materials = classroom.materials ?? []
  const hasPrompts = Boolean(classroom.starterPrompts && classroom.starterPrompts.length > 0)
  const [value, setValue] = useState('')
  const [filePreview, setFilePreview] = useState<{ src: string; name: string; mimeType: string } | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const { textareaRef, resize } = useAutoResizeTextarea()
  const { attachments, setAttachments, addFiles, removeAttachment, clearAttachments, isUploadingAttachments } = useAttachmentUpload()
  const hasIncompleteAttachments = attachments.some((attachment) => attachment.uploadStatus !== 'uploaded')
  const canSend = !isEnded && !isReadOnlyAdmin && (value.trim().length > 0 || attachments.length > 0) && !hasIncompleteAttachments
  const starterPromptStyles = useMemo(
    () =>
      (classroom.starterPrompts ?? []).map((prompt) => {
        const palette = getClassroomPalette({ title: prompt.label })
        return {
          backgroundColor: isDark ? palette.dark : palette.light,
          color: isDark ? '#ffffff' : '#111111',
        }
      }),
    [classroom.starterPrompts, isDark],
  )

  const handleSend = useCallback(() => {
    if (isEnded || isReadOnlyAdmin) return
    const content = value.trim()
    if (!content && attachments.length === 0) return
    chatInputActionsRef.current.onSend?.(content, attachments)
    setValue('')
    clearAttachments()
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [attachments, clearAttachments, isEnded, isReadOnlyAdmin, textareaRef, value])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const handleFileUpload = useCallback(() => {
    if (isEnded || isReadOnlyAdmin) return
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.accept = ATTACHMENT_ACCEPT
    input.onchange = async () => {
      if (!input.files) return
      await addFiles(Array.from(input.files))
    }
    input.click()
  }, [addFiles, isEnded, isReadOnlyAdmin])

  const handleMaterialSelect = useCallback((material: ClassroomMaterial) => {
    if (isEnded || isReadOnlyAdmin) return
    if (!material?.attachment) {
      toast.error('This material does not have an attachment yet.')
      return
    }
    const attachment = material.attachment

    setAttachments((prev) => {
      if (prev.some((item) => item.storageId === attachment.storageId)) {
        return prev
      }

      const nextAttachment: ChatAttachment = {
        id: generateId(),
        name: attachment.filename,
        type: attachment.mimeType,
        size: attachment.sizeBytes ?? 0,
        url: buildStorageFileUrl(attachment.storageName || attachment.storageId),
        storageId: attachment.storageId,
        storageName: attachment.storageName,
        uploadStatus: 'uploaded',
        uploadProgress: 100,
        encoding: attachment.mimeType.startsWith('image/') ? 'base64' : 'text',
      }

      return [...prev, nextAttachment]
    })
    textareaRef.current?.focus()
  }, [isEnded, isReadOnlyAdmin, setAttachments, textareaRef])

  const openMaterialPreview = useCallback((material: ClassroomMaterial) => {
    if (!material.attachment) return
    const src = buildStorageFileUrl(material.attachment.storageName || material.attachment.storageId)
    if (material.attachment.mimeType.startsWith('image/')) {
      setImagePreview(src)
      return
    }
    if (!isPreviewableFile(material.attachment.mimeType, material.attachment.filename)) return
    setFilePreview({
      src,
      name: material.attachment.filename,
      mimeType: material.attachment.mimeType,
    })
  }, [])

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto grid max-w-[1120px] gap-6 px-5 py-6 xl:grid-cols-[minmax(0,1fr)_280px]">
        <div className="min-w-0">
          <div className="mb-6">
            <h1 className="font-heading text-3xl text-foreground">{classroom.title}</h1>
            <p className="mt-2 text-sm text-foreground/60">
              {classroom.description || classroom.topics.join(', ') || 'No description yet'}
            </p>
          </div>

          {isReadOnlyAdmin ? (
            <div className="mb-6 max-w-[560px] rounded-base border-2 border-nb-border bg-secondary-bg p-4 nb-shadow">
              <p className="text-sm text-foreground/60">
                Admins can review this class and its materials, but cannot create new chats or send messages here.
              </p>
            </div>
          ) : (
            <div className="mb-6 max-w-[560px] rounded-base border-2 border-nb-border bg-secondary-bg nb-shadow">
              <AttachmentList attachments={attachments} onRemove={removeAttachment} />

              <div className="px-4 pt-3 pb-2">
                <textarea
                  ref={textareaRef}
                  className="min-h-[52px] max-h-[200px] w-full resize-none bg-transparent text-base text-foreground outline-none placeholder:text-foreground/40"
                  placeholder="Ask me anything..."
                  rows={2}
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value)
                    resize()
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={isEnded}
                />
              </div>
              <div className="flex items-center justify-between border-t-2 border-nb-border px-3 py-2">
                <div className="flex items-center gap-2">
                  <ClassMaterialsDropdown materials={materials} onSelect={handleMaterialSelect} />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="rounded-base border-2 border-nb-border bg-bg p-1.5 text-foreground transition-colors hover:bg-main hover:text-main-foreground"
                        onClick={handleFileUpload}
                        type="button"
                        disabled={isEnded}
                      >
                        <Paperclip className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Upload attachment</TooltipContent>
                  </Tooltip>
                </div>
                <button
                  className={cn(
                    'rounded-base border-2 border-nb-border p-1.5 font-heading transition-all',
                    canSend
                      ? 'bg-main text-main-foreground nb-shadow-sm nb-hover-press'
                      : 'cursor-not-allowed bg-foreground/10 text-foreground/30',
                  )}
                  disabled={!canSend}
                  onClick={handleSend}
                  type="button"
                  title={
                    isUploadingAttachments
                      ? 'Wait for attachments to finish uploading'
                      : hasIncompleteAttachments
                        ? 'Remove failed attachments before sending'
                        : isEnded
                          ? 'This class has ended'
                          : 'Send message'
                  }
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <p className="mb-5 text-sm text-foreground/55">
            {isEnded
              ? 'This class has ended. You can still review existing conversations and materials.'
              : 'Start a chat to keep conversations organized and re-use project knowledge.'}
          </p>

          {hasPrompts ? (
            <div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {classroom.starterPrompts?.map((prompt, index) => (
                  <button
                    key={prompt.label}
                    className="rounded-base border-2 border-nb-border px-4 py-3.5 text-left text-sm font-heading transition-all nb-shadow-sm nb-hover-press"
                    style={starterPromptStyles[index]}
                    disabled={isEnded || isReadOnlyAdmin}
                    onClick={() => onStarterPromptClick?.(prompt.prompt)}
                  >
                    {prompt.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <aside className="rounded-base border-2 border-nb-border bg-secondary-bg p-4 nb-shadow xl:self-start">
          <div className="mb-4 inline-flex items-center gap-2 text-[11px] font-heading uppercase tracking-[0.18em] text-foreground/45">
            <LibraryBig className="size-4" />
            Materials
          </div>

          {materials.length === 0 ? (
            <div className="rounded-base border-2 border-dashed border-nb-border bg-bg p-4 text-sm text-foreground/45">
              No materials yet.
            </div>
          ) : (
            <div className="space-y-2">
              {materials.map((material) => (
                <button
                  key={material.id ?? material.title}
                  type="button"
                  className="w-full rounded-base border-2 border-nb-border bg-bg p-3 text-left transition-colors hover:bg-main/10"
                  onClick={() => openMaterialPreview(material)}
                  disabled={!material.attachment || (!material.attachment.mimeType.startsWith('image/') && !isPreviewableFile(material.attachment.mimeType, material.attachment.filename))}
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-heading text-foreground">{material.title}</p>
                      <p className="mt-0.5 text-[11px] text-foreground/50">
                        {material.kind
                          ? material.kind.charAt(0).toUpperCase() + material.kind.slice(1).toLowerCase()
                          : 'Material'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>
      {filePreview && (
        <FilePreview
          src={filePreview.src}
          fileName={filePreview.name}
          mimeType={filePreview.mimeType}
          open={true}
          onClose={() => setFilePreview(null)}
        />
      )}
      {imagePreview && (
        <ImagePreview
          src={imagePreview}
          open={true}
          onClose={() => setImagePreview(null)}
        />
      )}
    </div>
  )
}
